<template>
    <RedesignBaseModal
        :visible="showDialog"
        :top="'8vh'"
        append-to-body
        width="520px"
        wrapper-classes="full-height reorder-groups-modal"
        @update:visible="onChangeVisible"
        @close="closeModal"
    >
        <template #title>
            <div class="text-xl px-2">
                {{ $t('tooltip.reorder.dashboard.layout') }}
            </div>
        </template>
        <div class="py-4">
            <DraggableList
                v-if="showDraggableList"
                :value="widgetGroups"
                group="widgets"
                @change="onGroupListChange"
            >
                <div
                    v-for="(widgetGroup, index) in widgetGroups"
                    :key="index"
                    class="w-full flex justify-between items-center items my-1 shadow-base border border-ui-lines p-2 rounded"
                >
                    <div
                        :style="getStyles"
                        class="widget-item p-1"
                    >
                        {{ widgetGroup.WidgetGroupTitle || `# ${widgetGroup.WidgetGroupID}` }}
                    </div>
                    <div class="flex cursor-pointer">
                        <RedesignBaseIcon
                            name="GripVertical"
                            :size="24"
                            class="text-secondary-actions"
                        />
                    </div>
                </div>
            </DraggableList>
        </div>
        <template #footer>
            <div class="w-full flex items-center justify-between">
                <RedesignBaseButton
                    type="outline"
                    color="secondary"
                    icon="X"
                    @click="closeModal"
                >
                    {{ $t('common.cancel') }}
                </RedesignBaseButton>
                <RedesignBaseButton
                    :loading="loading"
                    icon="Save"
                    @click="confirm"
                >
                    {{ $t('common.save') }}
                </RedesignBaseButton>
            </div>
        </template>
    </RedesignBaseModal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'
import { useConfirmBeforeClose } from '@/composables/useConfirmBeforeClose'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import DraggableList from '@/components/Widgets/DraggableList.vue'
import { WidgetGroupList } from '@/types/dashboard'
import { widgetGroupTitleStyles } from '@/composables/globalData/useLayouts'
import cloneDeep from 'lodash/cloneDeep.js'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'

type TDraggableListChangeEvent = {
    moved: {
        element: WidgetGroupList
        newIndex: number
        oldIndex: number
    }
}

defineProps<{
    loading?: boolean
}>()

const emit = defineEmits<{
    (e: 'confirm', data: Array<WidgetGroupList>): void
}>()

const { closeWithConfirm } = useConfirmBeforeClose()
const showDialog = ref(false)
const showDraggableList = ref(false)
const widgetGroups = ref<Array<WidgetGroupList>>([])

const getStyles = computed(() => {
    return widgetGroupTitleStyles.value
})

function onChangeVisible (state: boolean) {
    showDialog.value = state
}

function forceCloseModal () {
    showDialog.value = false
}

function closeModal () {
    closeWithConfirm(forceCloseModal)
}

function openModal (groupList: Array<WidgetGroupList>) {
    widgetGroups.value = cloneDeep(groupList)
    showDialog.value = true
    nextTick(() => {
        showDraggableList.value = true
    })
}

function confirm () {
    emit('confirm', widgetGroups.value.map((item, index) => {
        return {
            ...item,
            Order: index
        }
    }))
}

function onGroupListChange (data: TDraggableListChangeEvent) {
    if (data.moved) {
        const eventData = data.moved
        const { newIndex: newIndex, oldIndex: oldIndex } = eventData

        widgetGroups.value.splice(newIndex, 0, widgetGroups.value.splice(oldIndex, 1)[0])
    }
}

defineExpose({
    open: openModal,
    close: forceCloseModal
})
</script>

